import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
export const COMPOSE_DRAFT_SUGGESTION_ID = '__compose_draft__';
export const isRangeInsideComposeDraft = (from, to, draft) => {
    return from >= draft.from && to <= draft.to;
};
export const mapIntentThroughComposeRestore = (intent, draft) => {
    if (intent.to <= draft.from) {
        return intent;
    }
    if (intent.from >= draft.to) {
        const mappedFrom = intent.from - draft.to + draft.apiTo;
        const mappedTo = intent.to - draft.to + draft.apiTo;
        return {
            ...intent,
            from: mappedFrom,
            to: mappedTo,
        };
    }
    return {
        ...intent,
        from: draft.apiFrom,
        to: draft.apiFrom,
        originalText: '',
    };
};
const getSelectionRange = (view) => {
    const { state } = view;
    const { selection } = state;
    if (!(selection instanceof TextSelection)) {
        return null;
    }
    return {
        from: selection.from,
        to: selection.to,
        text: state.doc.textBetween(selection.from, selection.to),
    };
};
const getDeleteRange = (view, direction) => {
    const selected = getSelectionRange(view);
    if (!selected) {
        return null;
    }
    if (selected.from !== selected.to) {
        return selected;
    }
    const caret = selected.from;
    if (direction === 'backward') {
        if (caret <= 0) {
            return null;
        }
        const from = caret - 1;
        const text = view.state.doc.textBetween(from, caret);
        if (!text) {
            return null;
        }
        return { from, to: caret, text };
    }
    const docSize = view.state.doc.content.size;
    if (caret >= docSize) {
        return null;
    }
    const to = caret + 1;
    const text = view.state.doc.textBetween(caret, to);
    if (!text) {
        return null;
    }
    return { from: caret, to, text };
};
export const SuggestOnEdit = Extension.create({
    name: 'suggestOnEdit',
    addOptions() {
        return {
            isEnabled: () => true,
            getComposeDraftRange: undefined,
            onSuggestionIntent: undefined,
        };
    },
    addProseMirrorPlugins() {
        const options = this.options;
        const startReplacementOrInsert = (view, suggestionText, trigger) => {
            if (!(options.isEnabled?.() ?? true)) {
                return false;
            }
            const selected = getSelectionRange(view);
            if (!selected) {
                return true;
            }
            if (!suggestionText && selected.from === selected.to) {
                return false;
            }
            const draft = options.getComposeDraftRange?.() ?? null;
            if (draft && isRangeInsideComposeDraft(selected.from, selected.to, draft)) {
                return false;
            }
            options.onSuggestionIntent?.({
                from: selected.from,
                to: selected.to,
                originalText: selected.text,
                suggestionText,
                trigger,
            });
            return true;
        };
        const startDelete = (view, direction) => {
            if (!(options.isEnabled?.() ?? true)) {
                return false;
            }
            const range = getDeleteRange(view, direction);
            if (!range) {
                return true;
            }
            const draft = options.getComposeDraftRange?.() ?? null;
            if (draft && isRangeInsideComposeDraft(range.from, range.to, draft)) {
                return false;
            }
            options.onSuggestionIntent?.({
                from: range.from,
                to: range.to,
                originalText: range.text,
                suggestionText: '',
                trigger: 'delete',
            });
            return true;
        };
        return [
            new Plugin({
                key: new PluginKey('suggestOnEdit'),
                props: {
                    handleDOMEvents: {
                        beforeinput: (view, event) => {
                            const inputEvent = event;
                            if (!(options.isEnabled?.() ?? true)) {
                                return false;
                            }
                            let handled = false;
                            switch (inputEvent.inputType) {
                                case 'insertText': {
                                    const text = inputEvent.data ?? '';
                                    if (!text) {
                                        return false;
                                    }
                                    handled = startReplacementOrInsert(view, text, 'text');
                                    break;
                                }
                                case 'insertParagraph':
                                case 'insertLineBreak':
                                    handled = startReplacementOrInsert(view, '\n', 'text');
                                    break;
                                case 'deleteContentBackward':
                                    handled = startDelete(view, 'backward');
                                    break;
                                case 'deleteContentForward':
                                    handled = startDelete(view, 'forward');
                                    break;
                                default:
                                    return false;
                            }
                            if (handled) {
                                event.preventDefault();
                            }
                            return handled;
                        },
                    },
                    handleTextInput: (view, _from, _to, text) => startReplacementOrInsert(view, text, 'text'),
                    handlePaste: (view, event) => {
                        const pastedText = event.clipboardData?.getData('text/plain') ?? '';
                        if (!pastedText) {
                            return false;
                        }
                        return startReplacementOrInsert(view, pastedText, 'paste');
                    },
                    handleKeyDown: (view, event) => {
                        if (event.key === 'Backspace') {
                            return startDelete(view, 'backward');
                        }
                        if (event.key === 'Delete') {
                            return startDelete(view, 'forward');
                        }
                        return false;
                    },
                },
            }),
        ];
    },
});
//# sourceMappingURL=suggest-on-edit.js.map